
import { useState, useMemo } from 'react';

interface LeadLike {
  name: string;
  category?: string | null;
  _shareName?: string;
  [key: string]: any;
}

export function useLeadFilters<T extends LeadLike>(leads: T[]) {
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedShareGroups, setSelectedShareGroups] = useState<string[]>([]);
  const [view, setView] = useState<'table' | 'card'>('table');

  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    leads.forEach((l) => {
      if (l.category) cats.add(l.category);
    });
    return Array.from(cats).sort();
  }, [leads]);

  const availableShareGroups = useMemo(() => {
    const groups = new Set<string>();
    leads.forEach((l) => {
      if (l._shareName) groups.add(l._shareName);
    });
    return Array.from(groups).sort();
  }, [leads]);

  const filteredLeads = useMemo(() => {
    let result = leads;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((l) => l.name.toLowerCase().includes(q));
    }

    if (selectedCategories.length > 0) {
      result = result.filter(
        (l) => l.category && selectedCategories.includes(l.category)
      );
    }

    if (selectedShareGroups.length > 0) {
      result = result.filter(
        (l) => l._shareName && selectedShareGroups.includes(l._shareName)
      );
    }

    result = [...result].sort((a, b) =>
      sortDir === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    );

    return result;
  }, [leads, search, sortDir, selectedCategories, selectedShareGroups]);

  return {
    search,
    setSearch,
    sortDir,
    setSortDir,
    selectedCategories,
    setSelectedCategories,
    selectedShareGroups,
    setSelectedShareGroups,
    view,
    setView,
    availableCategories,
    availableShareGroups,
    filteredLeads,
  };
}
